import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import { compatWrapper } from '@backstage/core-compat-api';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
  EntityContentLayoutBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import { hasSubRole, SubRoleKey } from '../../components/catalog/SubRoleMembersCard';
import { onVariants } from './variants';

/** Relations shown on the system Diagram tab, in display order. */
export const systemDiagramRelations = [
  RELATION_PART_OF,
  RELATION_HAS_PART,
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_PROVIDES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
];

const customAboutCard = EntityCardBlueprint.make({
  name: 'custom-about',
  params: {
    type: 'content',
    filter: onVariants(['system']),
    loader: () =>
      import('../../components/catalog/CustomAboutCard').then(m =>
        compatWrapper(<m.CustomAboutCard variant="gridItem" />),
      ),
  },
});

const userProfileCard = EntityCardBlueprint.make({
  name: 'user-profile',
  params: {
    type: 'content',
    filter: onVariants(['user']),
    loader: () =>
      import('../../components/catalog/CustomUserProfileCard').then(m =>
        compatWrapper(<m.CustomUserProfileCard variant="gridItem" />),
      ),
  },
});

const groupProfileCard = EntityCardBlueprint.make({
  name: 'group-profile',
  params: {
    type: 'content',
    filter: onVariants(['group']),
    loader: () =>
      import('../../components/catalog/EnhancedGroupProfileCard').then(m =>
        compatWrapper(<m.EnhancedGroupProfileCard variant="gridItem" showLinks />),
      ),
  },
});

const membersListCard = EntityCardBlueprint.make({
  name: 'members-list',
  params: {
    type: 'content',
    filter: onVariants(['group']),
    loader: () =>
      import('../../components/catalog/CustomMembersListCard').then(m =>
        compatWrapper(<m.CustomMembersListCard />),
      ),
  },
});

const subRoleCard = (name: string, roleKey: SubRoleKey, title: string) =>
  EntityCardBlueprint.make({
    name,
    params: {
      type: 'content',
      filter: onVariants(['group'], hasSubRole(roleKey)),
      loader: () =>
        import('../../components/catalog/SubRoleMembersCard').then(m =>
          compatWrapper(<m.SubRoleMembersCard roleKey={roleKey} title={title} />),
        ),
    },
  });

const mcpServerRemotesCard = EntityCardBlueprint.make({
  name: 'mcp-server-remotes',
  params: {
    type: 'content',
    filter: onVariants(['mcp-server']),
    loader: () =>
      import('../../components/catalog/McpServerRemotesCard').then(m =>
        compatWrapper(<m.McpServerRemotesCard />),
      ),
  },
});

const aiResourceDetailsCard = EntityCardBlueprint.make({
  name: 'ai-resource-details',
  params: {
    type: 'content',
    filter: onVariants(['airesource']),
    loader: () =>
      import('../../components/catalog/AiResourceDetailsCard').then(m =>
        compatWrapper(<m.AiResourceDetailsCard />),
      ),
  },
});

const dependenciesContent = EntityContentBlueprint.make({
  name: 'dependencies',
  params: {
    path: '/dependencies',
    title: 'Dependencies',
    filter: onVariants(['service', 'website']),
    loader: () =>
      import('./DependenciesContent').then(m =>
        compatWrapper(<m.DependenciesContent />),
      ),
  },
});

const diagramContent = EntityContentBlueprint.make({
  name: 'diagram',
  params: {
    path: '/diagram',
    title: 'Diagram',
    filter: onVariants(['system']),
    loader: () =>
      import('@backstage/plugin-catalog-graph').then(m =>
        compatWrapper(
          <m.EntityCatalogGraphCard
            direction={m.Direction.TOP_BOTTOM}
            title="System Diagram"
            height={700}
            relations={systemDiagramRelations}
            unidirectional={false}
          />,
        ),
      ),
  },
});

// Overview cards keep the production (legacy entity page) grid: position,
// width and order per entity kind. See OverviewLayout.tsx.
const overviewLayout = EntityContentLayoutBlueprint.make({
  name: 'legacy-grid',
  params: {
    loader: () =>
      import('./OverviewLayout').then(m => m.LegacyGridOverviewLayout),
  },
});

/** Entity page cards and tabs owned by the portal itself. */
export const appEntityPageModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    customAboutCard,
    userProfileCard,
    groupProfileCard,
    membersListCard,
    subRoleCard('dev-leads', 'devLeads', 'Dev Leads'),
    subRoleCard('qa-leads', 'qaLeads', 'QA Leads'),
    subRoleCard('devops', 'devops', 'DevOps'),
    mcpServerRemotesCard,
    aiResourceDetailsCard,
    dependenciesContent,
    diagramContent,
    overviewLayout,
  ],
});
